import { useEffect, type ReactNode } from 'react';
import { Button, Center, Loader, Stack, Text } from '@mantine/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { Link, Redirect, Route, Switch, useLocation, useSearch } from 'wouter';
import { KnowledgeProvider } from '../features/knowledge/KnowledgeDrawer';
import { SplashPage } from '../features/splash/SplashPage';
import { DashboardPage } from '../features/tournaments/DashboardPage';
import { TournamentPage, roundHref } from '../features/tournaments/TournamentPage';
import { RoundPage } from '../features/round/RoundPage';
import { SettingsPage } from '../features/settings/SettingsPage';
import { useAccess, useAccount } from '../features/account/AccountProvider';
import { UpdatePrompt } from './UpdatePrompt';
import { InstallHint } from './InstallHint';
import { AccountServices, useDeps, useServices, useUseCases } from './services';
import { useTabroomSync } from '../features/tabroom/useTabroom';
import { requestPersistentStorage } from '../infra/browser';
import { PageShell } from '../ui/PageShell';
import { fontVars } from '../ui/fonts';
import { paths, ROUND_TABS, SETTINGS_SECTIONS, type RoundTab, type SettingsSection } from './routes';

/**
 * Signed-in only (invite only). Devices with a confirmed account open straight away, offline too, and re-check
 * in the background; anyone else goes to the front page, which brings them back here after signing in.
 */
const Gate = ({ children }: { children: ReactNode }) => {
  const access = useAccess();
  const { state, known } = useAccount();
  const [location] = useLocation();
  const search = useSearch();
  const accountId = (state.status === 'signedIn' ? state.user?.id : null) ?? known?.id;
  // Keyed by account: switching accounts closes one account's data before opening the other's.
  if (access === 'allowed' && accountId) return <AccountServices key={accountId} accountId={accountId}>{children}</AccountServices>;
  if (access === 'checking') {
    const problem = state.status === 'signedIn' ? state.problem : null;
    return (
      <Center mih="100dvh">
        <Stack align="center" gap="xs">
          {problem ? <Text size="sm" maw={360} ta="center">{problem}</Text> : <Loader size="sm" />}
        </Stack>
      </Center>
    );
  }
  const next = `${location}${search ? `?${search}` : ''}`;
  return <Redirect to={`${paths.splash}?next=${encodeURIComponent(next)}`} replace />;
};

/** Background work for a signed-in device. */
const SignedIn = ({ children }: { children: ReactNode }) => {
  const uc = useUseCases();
  const deps = useDeps();
  // The judge's font choice (Settings → Display), as CSS variables on <html> (ui/fonts.ts; defaults in ui/global.css).
  const settings = useLiveQuery(() => deps.settings.get(), []);
  const uiFont = settings?.uiFont;
  const writingFont = settings?.writingFont;
  useEffect(() => {
    if (!uiFont || !writingFont) return;
    const el = document.documentElement;
    const vars = fontVars(uiFont, writingFont);
    for (const [k, v] of Object.entries(vars)) el.style.setProperty(k, v);
    return () => {
      for (const k of Object.keys(vars)) el.style.removeProperty(k);
    };
  }, [uiFont, writingFont]);
  useTabroomSync(); // owner only: new Tabroom assignments become rounds
  // Opening a page shows what's on this device at once, then catches up with other devices (a pull on every page).
  const [path] = useLocation();
  const { sync } = useServices();
  useEffect(() => void sync.run(), [path, sync]);
  useEffect(() => {
    // Ask the browser not to evict our data, and purge debater names past their retention window.
    void requestPersistentStorage();
    void uc.purgeExpiredNames();
  }, [uc]);
  return <>{children}</>;
};

/** Links from before URLs had the tournament in them (/round/:id), e.g. old notifications and bookmarks. */
const LegacyRound = ({ id }: { id: string }) => {
  const deps = useDeps();
  const round = useLiveQuery(async () => (await deps.rounds.get(id)) ?? null, [id]);
  if (round === undefined) return null;
  return <Redirect to={round ? roundHref(round) : paths.dashboard} replace />;
};

const NotFound = () => (
  <PageShell title="Page not found" crumbs={[{ label: 'Tournaments', href: paths.dashboard }]}>
    <Text mb="md">There's nothing at this address.</Text>
    <Button component={Link} href={paths.dashboard}>Your tournaments</Button>
  </PageShell>
);

const asTab = (v: string | undefined): RoundTab | null => (ROUND_TABS.includes(v as RoundTab) ? (v as RoundTab) : null);
const asSection = (v: string | undefined): SettingsSection | null => (SETTINGS_SECTIONS.includes(v as SettingsSection) ? (v as SettingsSection) : null);

export const App = () => (
  <>
    <Switch>
      <Route path="/" component={SplashPage} />
      <Route>
        <Gate>
          <SignedIn>
            <KnowledgeProvider>
              <UpdatePrompt />
              <InstallHint />
              <Switch>
                <Route path="/app" component={DashboardPage} />
                <Route path="/t/:tid">{(p) => <TournamentPage tid={p.tid} />}</Route>
                <Route path="/t/:tid/r/:rid">{(p) => <RoundPage tid={p.tid} rid={p.rid} tab={null} />}</Route>
                <Route path="/t/:tid/r/:rid/:tab">
                  {(p) => (asTab(p.tab) ? <RoundPage tid={p.tid} rid={p.rid} tab={asTab(p.tab)} /> : <Redirect to={paths.round(p.tid, p.rid)} replace />)}
                </Route>
                <Route path="/settings"><Redirect to={paths.settings('account')} replace /></Route>
                <Route path="/settings/:section">
                  {(p) => {
                    const section = asSection(p.section);
                    return section ? <SettingsPage section={section} /> : <Redirect to={paths.settings('account')} replace />;
                  }}
                </Route>
                <Route path="/round/:id">{(p) => <LegacyRound id={p.id} />}</Route>
                <Route path="/tournaments"><Redirect to={paths.dashboard} replace /></Route>
                <Route component={NotFound} />
              </Switch>
            </KnowledgeProvider>
          </SignedIn>
        </Gate>
      </Route>
    </Switch>
  </>
);
